import { useState } from 'react'
import { motion } from 'motion/react'
import { ChevronRight, LogOut, Monitor, Moon, RefreshCw, Sun, Users } from 'lucide-react'
import { Boton, Cabecera, Campo, Tarjeta } from '../components/ui'
import { api } from '../lib/api'
import { useSyncStatus } from '../lib/hooks'
import { useIrA } from '../lib/navegacion'
import { useSession } from '../lib/session'
import { sync } from '../lib/sync'
import { TEMAS, useTema, type Tema } from '../lib/tema'

const ESTADO: Record<string, string> = {
  idle: 'Al día',
  syncing: 'Sincronizando…',
  offline: 'Sin conexión: se guarda en el móvil',
  error: 'Falló la última sincronización',
}

export function Ajustes() {
  const { user, logout } = useSession()
  const irA = useIrA()
  const { status, pending } = useSyncStatus()
  const [invitacion, setInvitacion] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function invitar(): Promise<void> {
    setError(null)
    try {
      const data = await api.post<{ code: string }>('/api/auth/invite')
      setInvitacion(data.code)
    } catch (caught) {
      setError((caught as Error).message)
    }
  }

  return (
    <>
      <Cabecera titulo="Ajustes" />

      <Tarjeta className="mt-5 flex items-center gap-3">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-accent font-display text-xl text-primary">
          {user?.displayName.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold">{user?.displayName}</p>
          <p className="truncate text-sm text-muted-foreground">{user?.username}</p>
        </div>
      </Tarjeta>

      {user?.isAdmin && (
        <button
          type="button"
          onClick={() => irA('admin')}
          className="mt-3 flex min-h-14 w-full items-center gap-3 rounded-3xl border border-border/70 bg-card px-4 text-left outline-none transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Users className="h-5 w-5 text-primary" />
          <span className="flex-1 font-medium">Usuarios y hogares</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      <Seccion titulo="Apariencia">
        <ElegirTema />
      </Seccion>

      <Seccion titulo="Sincronización">
        <p className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${
              status === 'idle' ? 'bg-sage' : status === 'syncing' ? 'bg-apricot' : 'bg-primary'
            }`}
          />
          {ESTADO[status] ?? status}
        </p>
        {pending > 0 && (
          <p className="mt-1 text-sm text-muted-foreground">
            {pending} cambio{pending === 1 ? '' : 's'} esperando a que vuelva la cobertura.
          </p>
        )}
        <Boton ancho className="mt-3" onClick={() => void sync()}>
          <RefreshCw className={`h-4 w-4 ${status === 'syncing' ? 'animate-spin' : ''}`} />
          Sincronizar ahora
        </Boton>
      </Seccion>

      <Seccion titulo="Hogar">
        <p className="text-sm text-muted-foreground">
          Para que otra persona de casa entre con su propio usuario, dale un código. Caduca en 24
          horas.
        </p>
        {invitacion && (
          <p className="mt-3 rounded-2xl bg-filled px-4 py-3 text-center font-display text-2xl tracking-[0.2em] text-sage-deep">
            {invitacion}
          </p>
        )}
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <Boton ancho className="mt-3" onClick={invitar}>
          Generar código de invitación
        </Boton>
      </Seccion>

      <Seccion titulo="Contraseña">
        <CambiarContrasena />
      </Seccion>

      <Boton ancho variante="peligro" className="mt-6" onClick={() => void logout()}>
        <LogOut className="h-4 w-4" />
        Salir
      </Boton>
    </>
  )
}

const TEMA_INFO: Record<Tema, { label: string; Icono: typeof Sun }> = {
  sistema: { label: 'Automático', Icono: Monitor },
  claro: { label: 'Claro', Icono: Sun },
  oscuro: { label: 'Oscuro', Icono: Moon },
}

/** Automático sigue al teléfono: claro de día y oscuro de noche si él lo hace. */
function ElegirTema() {
  const { tema, elegir } = useTema()

  return (
    <div role="radiogroup" aria-label="Tema" className="grid grid-cols-3 gap-1 rounded-2xl bg-surface p-1">
      {TEMAS.map((valor) => {
        const { label, Icono } = TEMA_INFO[valor]
        const activo = tema === valor
        return (
          <button
            key={valor}
            type="button"
            role="radio"
            aria-checked={activo}
            onClick={() => elegir(valor)}
            className={`relative isolate flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
              activo ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {activo && (
              <motion.span
                layoutId="tema-activo"
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                className="absolute inset-0 -z-10 rounded-xl bg-card shadow-sm"
              />
            )}
            <Icono className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h2 className="label-caps mb-2 ml-1 font-sans text-muted-foreground">{titulo}</h2>
      <Tarjeta>{children}</Tarjeta>
    </section>
  )
}

/** La del `seed` es provisional, así que tiene que poder cambiarse desde aquí. */
function CambiarContrasena() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [estado, setEstado] = useState<'inicial' | 'hecho' | string>('inicial')

  async function cambiar(): Promise<void> {
    try {
      await api.post('/api/auth/password', { current, next })
      setCurrent('')
      setNext('')
      setEstado('hecho')
    } catch (caught) {
      setEstado((caught as Error).message)
    }
  }

  return (
    <>
      {estado === 'hecho' && (
        <p className="mb-3 rounded-2xl bg-filled px-4 py-3 text-sm text-sage-deep">Contraseña cambiada.</p>
      )}
      {estado !== 'inicial' && estado !== 'hecho' && <p className="mb-3 text-sm text-destructive">{estado}</p>}

      <Campo id="clave-actual" label="Actual">
        <input
          id="clave-actual"
          type="password"
          className="campo"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Campo>
      <Campo id="clave-nueva" label="Nueva (mínimo 8 caracteres)">
        <input
          id="clave-nueva"
          type="password"
          className="campo"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
      </Campo>
      <Boton ancho disabled={!current || next.length < 8} onClick={cambiar}>
        Cambiar contraseña
      </Boton>
    </>
  )
}
